import { getPdfRestApiUrl } from './get-pdf-rest-api-url'
import { getEpsBaseUrl } from './eps-base-url'
import { epsClient } from './eps-client'
import axios from 'axios'
const basePath = 'enrollments'

const enum PatchActions {
  OpenMedicare = 'OpenMedicare',
}

export const getEnrollmentPlatformBaseUrl = getEpsBaseUrl

const DEFAULT_PAGE_SIZE = 20
const MAX_PAGES = 100

export interface DocumentRequestsPageParams {
  page?: number
  limit?: number
}

export interface DocumentRequestsPageResult {
  data: unknown[]
  total?: number
  page?: number
  limit?: number
  hasMore?: boolean
}

/**
 * GET /api/v1/document-requests?employeePublicKey=epk&page=1&limit=20
 * Returns one page of document requests for the employee.
 */
export const getDocumentRequestsPage = async (
  employeePublicKey: string,
  params: DocumentRequestsPageParams = {}
): Promise<DocumentRequestsPageResult> => {
  const baseURL = getEnrollmentPlatformBaseUrl()
  const page = params.page ?? 1
  const limit = params.limit ?? DEFAULT_PAGE_SIZE
  const { data } = await axios.get('/api/v1/document-requests', {
    baseURL,
    params: { employeePublicKey, page, limit },
  })
  const items = Array.isArray(data) ? data : data?.data ?? data?.items ?? []
  const total = typeof data?.total === 'number' ? data.total : undefined
  const hasMore =
    data?.hasMore ??
    (typeof total === 'number' ? page * limit < total : items.length >= limit)
  return {
    data: items,
    total,
    page,
    limit,
    hasMore: Boolean(hasMore),
  }
}

/**
 * Fetches all document requests by following pagination until no more pages.
 */
export const getAllDocumentRequests = async (
  employeePublicKey: string
): Promise<unknown[]> => {
  const all: unknown[] = []
  let page = 1
  let hasMore = true
  while (hasMore && page <= MAX_PAGES) {
    const result = await getDocumentRequestsPage(employeePublicKey, {
      page,
      limit: DEFAULT_PAGE_SIZE,
    })
    const items = Array.isArray(result.data) ? result.data : []
    all.push(...items)
    hasMore = result.hasMore === true && items.length > 0
    page += 1
  }
  return all
}

/**
 * GET /api/v1/document-requests?employeePublicKey=epk
 * Returns document requests for the employee (first page only; use getAllDocumentRequests for all).
 * @deprecated Prefer getDocumentRequestsPage or getAllDocumentRequests for paginated APIs.
 */
export const getDocumentRequests = async (employeePublicKey: string) => {
  const result = await getDocumentRequestsPage(employeePublicKey, {
    page: 1,
    limit: DEFAULT_PAGE_SIZE,
  })
  return result.data as unknown[]
}

/**
 * POST /api/v1/document-uploads/presigned-upload-url
 * Body: { documentRequestId, fileName, contentType }
 * Returns: { uploadUrl, documentUploadId } (or similar from PresignedUploadUrlResponseDto)
 */
export const getDocumentPresignedUploadUrl = async (body: {
  documentRequestId: string
  fileName: string
  contentType: string
}) => {
  const baseURL = getEnrollmentPlatformBaseUrl()
  const { data } = await axios.post('/api/v1/document-requests/document-uploads/presigned-upload-url', body, {
    baseURL,
  })
  return data
}

export const markDocumentUploadComplete = async (
  documentUploadId: string,
  body: { sizeBytes: number; sha256: string }
) => {
  const baseURL = getEnrollmentPlatformBaseUrl()
  const { data } = await axios.post(
    `/api/v1/document-requests/document-uploads/${documentUploadId}/complete`,
    body,
    { baseURL }
  )
  return data
}

/**
 * PATCH /api/v1/document-requests/:documentRequestId/status
 * Body matches UpdateDocumentRequestStatusDto (e.g. { status: 'RESUBMITTED' }).
 */
export const updateDocumentRequestStatus = async (
  documentRequestId: string,
  body: { status: string }
): Promise<unknown> => {
  const baseURL = getEnrollmentPlatformBaseUrl()
  const { data } = await axios.patch(
    `/api/v1/document-requests/${encodeURIComponent(documentRequestId)}/status`,
    body,
    { baseURL }
  )
  return data
}

export const getUploadHistoryByCategory = async (employeePublicKey: string) => {
  const baseURL = getEnrollmentPlatformBaseUrl()
  const { data } = await axios.get('/api/v1/document-requests/document-uploads/history-by-category', {
    baseURL,
    params: { employeePublicKey },
  })
  const raw = data?.data ?? data
  return Array.isArray(raw) ? raw : []
}

export const getDocumentUploadDownloadUrl = async (documentUploadId) => {
  const baseURL = getEnrollmentPlatformBaseUrl()
  const url = `/api/v1/document-requests/document-uploads/${encodeURIComponent(documentUploadId)}/download`

  const { data } = await axios.get(url, { baseURL })
  if (!data?.url) {
    throw new Error('Missing presigned download url')
  }
  return data.url
}

/**
 * DELETE /api/v1/document-requests/document-uploads/:documentUploadId
 * Deletes a single document upload by id. Employees can only delete their own uploads.
 */
export const deleteDocumentUpload = async (
  documentUploadId: string
): Promise<void> => {
  const baseURL = getEnrollmentPlatformBaseUrl()
  await axios.delete(
    `/api/v1/document-requests/document-uploads/${encodeURIComponent(documentUploadId)}`,
    { baseURL }
  )
}

export interface ReplaceDocumentUploadDto {
  documentUploadId: string
  fileName: string
  contentType?: string
}

/**
 * POST /api/v1/document-requests/document-uploads/replace
 * Replaces a single document upload by id: deletes the existing upload and returns a new presigned URL.
 * Client uploads to that URL then calls markDocumentUploadComplete.
 */
export const replaceDocumentUpload = async (
  body: ReplaceDocumentUploadDto
): Promise<{ presignedUploadUrl?: string; uploadUrl?: string; preSignedUrl?: string; documentUploadId: string }> => {
  const baseURL = getEnrollmentPlatformBaseUrl()
  const { data } = await axios.post(
    '/api/v1/document-requests/document-uploads/replace',
    body,
    { baseURL }
  )
  return data
}


export const uploadFileToPresignedUrl = (url: string, file: File) =>
  axios.put(url, file, {
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
    transformRequest: [
      (data: unknown, headers: Record<string, string>) => {
        delete headers.Authorization
        return data
      },
    ],
  })

export const openMedicare = async (enrollmentPublicKey: string) => {
  const baseURL = getPdfRestApiUrl()
  return  axios.patch(
    `${basePath}/${enrollmentPublicKey}`,
    { action: PatchActions.OpenMedicare },
    {
      baseURL,
    }
  )
}

/**
 * Opens a cart for an enrollment
 * POST /api/v1/enrollment/:enrollmentPublicKey/cart/open
 * Sets cart status to 'New' and cartCommitted to false
 */
export const openCart = async (enrollmentPublicKey: string) => {
  // epsClient so the signed access_token cookie is sent (employee-experience host).
  return epsClient.post(`/api/v1/enrollment/${enrollmentPublicKey}/cart/open`, {})
}

export interface EffectiveContributionPerson {
  personPublicKey: string
  relationship: string
  contribution: number
  contributionFull: number
}

export interface EffectiveContributionGroup {
  id: string
  groupName: string
  stateProvince: string
  ratingArea: string
  persons: EffectiveContributionPerson[]
}

/**
 * GET /api/v1/enrollment/:enrollmentPublicKey/effective-contributions
 * Returns effective employer contributions per family group for an enrollment.
 * Contribution amounts are monthly (same units as the legacy Contribution fields).
 */
export const getEffectiveContributions = async (
  enrollmentPublicKey: string
): Promise<EffectiveContributionGroup[]> => {
  const baseURL = getEnrollmentPlatformBaseUrl()
  const { data } = await axios.get(
    `/api/v1/enrollment/${encodeURIComponent(
      enrollmentPublicKey
    )}/effective-contributions`,
    {
      baseURL,
    }
  )
  return data
}
